import {
  IconCalendar,
  IconSpeed,
  IconUser,
  IconWrench,
  IconChevron,
  IconEdit,
  IconTrash,
} from "./ServiceCardIcons";
import { formatJalaliDate, formatMileage, formatCost, getMechanicName } from "./jalaliUtils";
import { getNextServiceMileageFromDetails } from "../../../services/nextServiceMileage";

// Existing Next Service Mileage from the service record: details JSON
// first (frontend registration mirror), then common top-level aliases.
// Returns a positive finite number or null — never a fake/default value.
function resolveNextServiceMileage(service) {
  const fromDetails = getNextServiceMileageFromDetails(service?.details);
  if (fromDetails !== null) return fromDetails;
  const candidates = [
    service?.nextServiceMileage,
    service?.nextDueKm,
    service?.nextMileage,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function ServiceCard({ service, isOpen, onToggle, onEdit, onDelete }) {
  const jalaliDate = formatJalaliDate(service.serviceDate);
  const mileageStr = formatMileage(service.mileage);
  const mechanicName = getMechanicName(service);
  const description = service.description || "—";
  const nextServiceMileage = resolveNextServiceMileage(service);
  const nextServiceStr =
    nextServiceMileage !== null
      ? `${formatMileage(nextServiceMileage)} کیلومتر`
      : null;

  return (
    <div className={`completed-card${isOpen ? " is-open" : ""}`} dir="rtl">
      <div
        className="completed-card__header"
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="completed-card__type">
          <span className="completed-card__icon-box" aria-hidden>
            <IconWrench />
          </span>
          <span className="completed-card__type-text">{service.displayType}</span>
        </div>
        <span className="completed-card__chevron" aria-hidden>
          <IconChevron open={isOpen} />
        </span>
      </div>

      <div className="completed-card__summary">
        <span className="completed-card__summary-item">
          <IconCalendar />
          {jalaliDate}
        </span>
        <span className="completed-card__summary-sep" aria-hidden>
          •
        </span>
        <span className="completed-card__summary-item">
          <IconSpeed />
          {mileageStr} کیلومتر
        </span>
        {nextServiceStr !== null && (
          <>
            <span className="completed-card__summary-sep" aria-hidden>
              •
            </span>
            <span className="completed-card__summary-item">
              <IconSpeed />
              سرویس بعدی: {nextServiceStr}
            </span>
          </>
        )}
      </div>

      <div className={`completed-card__expand${isOpen ? " is-open" : ""}`} aria-hidden={!isOpen}>
        <div className="completed-card__expand-inner">
          <div className="completed-card__grid">
            <div className="completed-card__meta">
              <span className="completed-card__meta-icon" aria-hidden>
                <IconWrench />
              </span>
              <span className="completed-card__meta-label">نوع سرویس</span>
              <span className="completed-card__meta-value">{service.displayType}</span>
            </div>

            <div className="completed-card__meta">
              <span className="completed-card__meta-icon" aria-hidden>
                <IconCalendar />
              </span>
              <span className="completed-card__meta-label">تاریخ سرویس</span>
              <span className="completed-card__meta-value">{jalaliDate}</span>
            </div>

            <div className="completed-card__meta">
              <span className="completed-card__meta-icon" aria-hidden>
                <IconSpeed />
              </span>
              <span className="completed-card__meta-label">کیلومتر</span>
              <span className="completed-card__meta-value">{mileageStr} کیلومتر</span>
            </div>

            <div className="completed-card__meta">
              <span className="completed-card__meta-icon" aria-hidden>
                <IconSpeed />
              </span>
              <span className="completed-card__meta-label">سرویس بعدی</span>
              <span className="completed-card__meta-value">
                {nextServiceStr !== null ? nextServiceStr : "—"}
              </span>
            </div>

            <div className="completed-card__meta">
              <span className="completed-card__meta-icon" aria-hidden>
                <IconWrench />
              </span>
              <span className="completed-card__meta-label">هزینه</span>
              <span className="completed-card__meta-value">{formatCost(service.cost)}</span>
            </div>

            <div className="completed-card__meta">
              <span className="completed-card__meta-icon" aria-hidden>
                <IconUser />
              </span>
              <span className="completed-card__meta-label">مکانیک</span>
              <span className="completed-card__meta-value">{mechanicName}</span>
            </div>

            <div className="completed-card__meta completed-card__meta--full">
              <span className="completed-card__meta-label">توضیحات</span>
              <span className="completed-card__meta-value completed-card__meta-value--wrap">
                {description}
              </span>
            </div>
          </div>

          <div className="completed-card__actions">
            <button
              type="button"
              className="completed-card__btn completed-card__btn--edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <IconEdit />
              ویرایش
            </button>
            <button
              type="button"
              className="completed-card__btn completed-card__btn--delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <IconTrash />
              حذف
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServiceCard;
